import { getLocalizedText } from './localization';

import type { QuestionAnswerFormValues } from '@/pages/SurveyAnswer/SurveyAnswer/validation';
import type { AnswerOptionSelection, ReviewQuestionAnswer } from '@/types/review/ReviewAnswer';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { QuestionType } from '@/constants/QuestionType';

export interface GetSelectedValuesFromExistingParams {
    question: ReviewQuestion;
    existingAnswer: ReviewQuestionAnswer;
}

export interface GetCurrentSelectedValuesParams {
    currentAnswer: QuestionAnswerFormValues;
    currentQuestion: ReviewQuestion;
}

const getSelectedValuesFromExisting = ({
    question,
    existingAnswer,
}: GetSelectedValuesFromExistingParams): string[] => {
    if (existingAnswer.textValue && !question.hasOtherOption) {
        return [existingAnswer.textValue];
    }

    if (existingAnswer.intValue !== undefined && existingAnswer.intValue !== null) {
        return [String(existingAnswer.intValue)];
    }

    if (question?.questionType === QuestionType.YES_NO) {
        if (existingAnswer.boolValue !== undefined && existingAnswer.boolValue !== null) {
            return existingAnswer.boolValue ? ['yes'] : ['no'];
        }
    }

    if (existingAnswer.selectedOptions?.length) {
        const questionWithOptions = existingAnswer.reviewQuestion ?? question;
        const getOptionById = (optionId: string) =>
            questionWithOptions?.reviewQuestionOptions?.find((opt) => opt.id === optionId);

        if (question?.questionType === QuestionType.RANKING) {
            // Sort by sortOrder to preserve ranking from backend
            return [...existingAnswer.selectedOptions]
                .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                .map((option) => {
                    const questionOption =
                        option.reviewQuestionOption ?? getOptionById(option.reviewQuestionOptionId);
                    return questionOption?.id ?? '';
                })
                .filter(Boolean);
        }
        if (question?.questionType === QuestionType.YES_NO) {
            return existingAnswer.selectedOptions
                .map((option) => {
                    const questionOption =
                        option.reviewQuestionOption ?? getOptionById(option.reviewQuestionOptionId);
                    return getLocalizedText(questionOption?.label).toLowerCase() || '';
                })
                .filter(Boolean);
        }
        if (question?.questionType === QuestionType.EMOJI_CHOICE) {
            return existingAnswer.selectedOptions.map((option) => {
                const optionId = option.reviewQuestionOption?.id ?? option.reviewQuestionOptionId;
                const index =
                    question.reviewQuestionOptions?.findIndex((opt) => opt.id === optionId) ?? -1;
                return `emoji-${index}`;
            });
        }
        return existingAnswer.selectedOptions
            .map((option) => {
                const questionOption =
                    option.reviewQuestionOption ?? getOptionById(option.reviewQuestionOptionId);
                return questionOption?.id ?? '';
            })
            .filter(Boolean);
    }

    return [];
};

const getCurrentSelectedValues = ({
    currentAnswer,
    currentQuestion,
}: GetCurrentSelectedValuesParams): string[] => {
    if (!currentAnswer) {
        return [];
    }

    if (currentQuestion?.questionType === QuestionType.PLAIN_TEXT) {
        return currentAnswer.textValue ? [currentAnswer.textValue] : [];
    }

    if (currentQuestion?.questionType === QuestionType.RATING_SCALE) {
        return currentAnswer.intValue !== undefined ? [String(currentAnswer.intValue)] : [];
    }

    if (currentQuestion?.questionType === QuestionType.YES_NO) {
        if (currentAnswer.boolValue === undefined) {
            return [];
        }
        return currentAnswer.boolValue ? ['yes'] : ['no'];
    }

    return currentAnswer.selectedValues || [];
};

export interface TransformToReviewQuestionAnswerParams {
    formValues: QuestionAnswerFormValues;
    reviewQuestion: ReviewQuestion;
    existingReviewQuestionAnswer?: ReviewQuestionAnswer;
    reviewAnswerId: string;
    hasBeenSkipped: boolean;
    comment?: string;
}

const transformToReviewQuestionAnswer = ({
    formValues,
    reviewQuestion,
    existingReviewQuestionAnswer,
    reviewAnswerId,
    hasBeenSkipped,
    comment,
}: TransformToReviewQuestionAnswerParams): ReviewQuestionAnswer => {
    const selectedOptions: AnswerOptionSelection[] = [];

    // Transform selectedValues back to selectedOptions based on question type
    if (formValues.selectedValues?.length > 0 && reviewQuestion.reviewQuestionOptions) {
        formValues.selectedValues.forEach((value, index) => {
            let matchedOption;

            switch (reviewQuestion.questionType) {
                case QuestionType.EMOJI_CHOICE: {
                    // selectedValues contains 'emoji-{index}'
                    const emojiIndex = parseInt(value.replace('emoji-', ''), 10);
                    matchedOption = reviewQuestion.reviewQuestionOptions?.[emojiIndex];
                    break;
                }
                case QuestionType.RANKING:
                    // selectedValues contains option IDs in ranked order
                    matchedOption = reviewQuestion.reviewQuestionOptions?.find(
                        (opt) => opt.id === value,
                    );
                    break;
                case QuestionType.YES_NO:
                    // selectedValues contains lowercase 'yes' or 'no'
                    matchedOption = reviewQuestion.reviewQuestionOptions?.find(
                        (opt) => getLocalizedText(opt.label).toLowerCase() === value,
                    );
                    break;
                default:
                    // For CHECKBOX, MULTI_CHOICE - selectedValues now contains option IDs
                    // This eliminates ambiguity between system and user "Other" options
                    matchedOption = reviewQuestion.reviewQuestionOptions?.find(
                        (opt) => opt.id === value,
                    );
                    break;
            }

            if (matchedOption) {
                const answerOption: Omit<AnswerOptionSelection, 'reviewQuestionAnswer'> = {
                    reviewQuestionAnswerId: existingReviewQuestionAnswer?.id ?? '',
                    reviewQuestionOptionId: matchedOption.id,
                    reviewQuestionOption: matchedOption,
                };

                // Set sortOrder for RANKING questions to represent rank position
                if (reviewQuestion.questionType === QuestionType.RANKING) {
                    answerOption.sortOrder = index;
                }

                selectedOptions.push(answerOption as AnswerOptionSelection);
            }
        });
    }

    return {
        ...existingReviewQuestionAnswer,
        reviewQuestionId: reviewQuestion.id ?? existingReviewQuestionAnswer?.reviewQuestionId ?? '',
        reviewQuestion: reviewQuestion,
        reviewAnswerId: reviewAnswerId,
        textValue: formValues.textValue,
        intValue: formValues.intValue,
        boolValue: formValues.boolValue,
        selectedOptions,
        hasBeenSkipped: hasBeenSkipped,
        comment,
    };
};

export { getSelectedValuesFromExisting, getCurrentSelectedValues, transformToReviewQuestionAnswer };
